import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useAuth, API } from "../App";
import PortalBottomNav from "../components/PortalBottomNav";
import PortalHeader from "../components/PortalHeader";

const DAYS = [
  { id: "mon", label: "Monday" },
  { id: "tue", label: "Tuesday" },
  { id: "wed", label: "Wednesday" },
  { id: "thu", label: "Thursday" },
  { id: "fri", label: "Friday" },
  { id: "sat", label: "Saturday" },
  { id: "sun", label: "Sunday" },
];

function isoToDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function isoFor(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function getThisMondayISO() {
  const today = new Date();
  const dow = today.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  const m = new Date(today);
  m.setDate(today.getDate() + diff);
  m.setHours(0, 0, 0, 0);
  return isoFor(m);
}

const VolunteerSchedulePage = () => {
  const { weekStart } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0);

  const monday = useMemo(() => isoToDate(weekStart), [weekStart]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(monday, i)),
    [monday],
  );

  const prevWeek = useMemo(() => isoFor(addDays(monday, -7)), [monday]);
  const nextWeek = useMemo(() => isoFor(addDays(monday, 7)), [monday]);
  const thisWeek = useMemo(() => getThisMondayISO(), []);

  const formatWeekRange = () => {
    const end = addDays(monday, 6);
    return `${monday.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  // Default selected day = today (if it falls within this week), else Monday
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = addDays(monday, i);
      if (
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
      ) {
        setSelectedDay(i);
        return;
      }
    }
    setSelectedDay(0);
  }, [monday]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const isAdmin = user?.role === "admin";
        const url = isAdmin
          ? `${API}/admin/volunteer-schedules/${weekStart}`
          : `${API}/guest/volunteer-schedule/${weekStart}`;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSchedule(res.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load the plan");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekStart, token, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#2E2A26]/40 bg-[#FBF7F1]">
        Loading…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#C85A38] p-6 text-center bg-[#FBF7F1]">
        {err}
      </div>
    );
  }
  if (!schedule) return null;

  const tasks = schedule.tasks || [];
  const selectedDayId = DAYS[selectedDay].id;
  const selectedDayDate = weekDates[selectedDay];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isSelectedToday =
    selectedDayDate.getFullYear() === today.getFullYear() &&
    selectedDayDate.getMonth() === today.getMonth() &&
    selectedDayDate.getDate() === today.getDate();

  // Rows for the selected day: tasks with anyone assigned that day
  const rowsForDay = tasks
    .map((t) => ({
      id: t.id,
      name: t.name,
      highlight: t.highlight,
      names: t.assignments?.[selectedDayId] || [],
    }))
    .filter((r) => r.names.length > 0);

  // Names to flag as "you" — match by first name (assignments are stored as names)
  const myFirstNames = [
    user?.profile?.preferred_name?.trim(),
    user?.profile?.first_name?.trim(),
    user?.name?.split(" ")[0]?.trim(),
  ].filter(Boolean).map((n) => n.toLowerCase());
  const isMe = (name) => myFirstNames.includes(name.trim().toLowerCase());

  return (
    <div className="min-h-screen bg-[#FBF7F1] pb-24 savi-bg-pattern" data-testid="volunteer-schedule-page">
      <PortalHeader title="Team plan" back={user?.role === "admin" ? "/admin" : "/dashboard"} testid="vs-header" />
      <div className="max-w-3xl mx-auto space-y-2.5 sm:space-y-4 p-3 sm:p-6">
        {/* Header strip — green to differentiate from the Events calendar */}
        <div className="bg-gradient-to-br from-[#5C6A4C] to-[#8C9A7C] rounded-xl sm:rounded-2xl p-2.5 sm:p-3 text-white">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate(`/volunteer-schedule/${prevWeek}`)}
                className="p-1 hover:bg-white/15 rounded-full transition-colors"
                aria-label="Previous week"
                data-testid="vs-prev-week"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate(`/volunteer-schedule/${thisWeek}`)}
                className="text-xs sm:text-sm font-medium px-2 py-1 hover:bg-white/15 rounded-md transition-colors min-w-[140px] text-center"
                title="Jump to this week"
                data-testid="vs-this-week"
              >
                {formatWeekRange()}
              </button>
              <button
                onClick={() => navigate(`/volunteer-schedule/${nextWeek}`)}
                className="p-1 hover:bg-white/15 rounded-full transition-colors"
                aria-label="Next week"
                data-testid="vs-next-week"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-white/80">
              Team Plan
            </div>
          </div>
        </div>

        {/* Day pills */}
        <div className="bg-white rounded-2xl border border-stone-100 p-1.5 sm:p-2">
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
            {DAYS.map((d, i) => {
              const dayDate = weekDates[i];
              const isSelected = i === selectedDay;
              const isTodayCell =
                dayDate.getFullYear() === today.getFullYear() &&
                dayDate.getMonth() === today.getMonth() &&
                dayDate.getDate() === today.getDate();
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedDay(i)}
                  className={`flex flex-col items-center py-1 sm:py-1.5 rounded-xl text-[10px] sm:text-xs transition-colors ${
                    isSelected
                      ? "bg-[#5C6A4C] text-white"
                      : "text-[#2E2A26] hover:bg-[#8C9A7C]/15"
                  }`}
                  data-testid={`vs-day-pill-${i}`}
                >
                  <span className="text-[9px] sm:text-[10px] tracking-[0.12em]">
                    {d.label.slice(0, 3).toUpperCase()}
                  </span>
                  <span
                    className={`text-sm sm:text-base font-medium ${
                      isTodayCell && !isSelected ? "text-[#5C6A4C]" : ""
                    }`}
                  >
                    {dayDate.getDate()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day card */}
        <div
          className={`rounded-2xl border ${
            isSelectedToday
              ? "border-[#5C6A4C]/60 bg-[#F3F5EF]"
              : "border-stone-100 bg-white"
          } overflow-hidden`}
          data-testid={`vs-day-${selectedDayId}`}
        >
          <div
            className={`px-3 sm:px-4 py-2 sm:py-2.5 flex items-baseline justify-between ${
              isSelectedToday ? "bg-[#8C9A7C]/15" : "bg-[#FBF7F1]"
            }`}
          >
            <div className="flex items-baseline gap-2">
              <span
                className={`font-medium ${
                  isSelectedToday ? "text-[#5C6A4C]" : "text-[#2E2A26]"
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.2rem" }}
              >
                {DAYS[selectedDay].label}
              </span>
              {isSelectedToday && (
                <span className="text-[9px] uppercase tracking-wider text-[#5C6A4C] font-medium">
                  Today
                </span>
              )}
            </div>
            <span className="text-[11px] text-[#2E2A26]/55">
              {selectedDayDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </span>
          </div>

          {rowsForDay.length === 0 ? (
            <div className="px-3 py-6 text-[11px] sm:text-xs text-[#2E2A26]/35 italic text-center">
              Nothing assigned for this day
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {rowsForDay.map((row) => {
                const youOnThisTask = row.names.some(isMe);
                return (
                  <li
                    key={row.id}
                    className={`px-3 sm:px-4 py-2.5 sm:py-3 flex items-start gap-3 ${
                      youOnThisTask ? "bg-[#8C9A7C]/10" : ""
                    }`}
                    data-testid={`vs-task-row-${row.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-[13px] sm:text-sm font-medium leading-tight ${
                          row.highlight ? "text-[#C85A38]" : "text-[#2E2A26]"
                        }`}
                      >
                        {row.name}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {row.names.map((n, idx) => {
                          const me = isMe(n);
                          return (
                            <span
                              key={`${row.id}-${idx}-${n}`}
                              className={`inline-flex items-center text-[10px] sm:text-[11px] px-2 py-0.5 rounded-full ${
                                me
                                  ? "bg-[#5C6A4C] text-white font-medium"
                                  : "bg-[#F4ECE2] text-[#2E2A26]"
                              }`}
                            >
                              {me ? "You" : n}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Empty-state hint when the whole week is empty */}
        {tasks.length === 0 && (
          <div className="text-center text-[11px] text-[#2E2A26]/40 italic px-3">
            The plan for this week hasn&apos;t been set yet.
          </div>
        )}
      </div>
      <PortalBottomNav />
    </div>
  );
};

export default VolunteerSchedulePage;
